import { Box, Tooltip, Typography } from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { MLAppConfig } from "../../../../../shared/app-configs/ml-app-configs";
import _ from "lodash";
import { StyledButton } from "../../../../../shared/components/buttons/custom-button";
import FormBuilder from "../../../../../shared/components/form-builder/form-builder";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../shared/components/inputfields/form-elements";
import { LoaderComponent } from "../../../../../shared/components/loaders/page-loader";
import AlertPopup from "../../../../../shared/components/modals/alert-moadal";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../shared/components/table/inline-edit-table";
import { useApi } from "../../../../../shared/hooks/useApi";
import { notify } from "../../../../../shared/utils/notify.utills";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";
import { dtrackItService } from "../../../../transaction-certificates/tc-service/dtrackit.service";
import {
  IInventoryUpdateRawMaterialData,
  IPostInventory,
} from "../models/inventory-models";
interface UpdateInventoryProps {
  formRef: any;
  inventoryData: IPostInventory;
  setInventoryData: (data: IPostInventory) => void;
  loading: Boolean;
  countryStateData: {
    country: string;
    state: string;
  };
  refreshInventory: () => void;
  coTEId: string;
  standards: string;
  organizationName: string; // New prop for organization name
  onModalClose?: () => void; // New prop to handle modal close
  certifiedRawMaterialCodes: any[];
}

const UpdateInventoryDetails: React.FC<UpdateInventoryProps> = ({
  formRef,
  inventoryData,
  setInventoryData,
  countryStateData,
  refreshInventory,
  loading,
  coTEId,
  standards,
  organizationName,
  certifiedRawMaterialCodes,
}) => {
  const [editInitialValues, setEditInitialValues] =
    useState<IPostInventory>(inventoryData);
  const [productCategory, setProductCategory] = useState([]);
  const [productDetails, setProductDetails] = useState([]);

  const [rawMaterialCodes, setRawMaterialCodes] = useState<any[]>([]);

  const [filteredStandards, setFilteredStandards] = useState<any[]>([]);
  const [filteredRMCodes, setFilteredRMCodes] = useState<any[]>([]);

  const [apiSuccess, setApiSuccess] = useState(false);

  const { executePost: inventoryupdatepost, loading: inventoryLoading } =
    useApi();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [popUpTitle, setPopUpTitle] = useState("");
  const [popUpMessage, setPopUpMessage] = useState("");
  const [popUpBtnName, setPopUpBtnName] = useState("");
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [volumeAdjustmentError, setVolumeAdjustmentError] = useState<
    string | null
  >(null);
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const { getUserInfo } = useTransactionCertificateStore();
  const [isRestrictPopUpSave, setIsRestrictPopUpSave] = useState(false);
  const { cbLicensingCode } = useTransactionCertificateStore();

  const { executeGet } = useApi();

  // --- NEW: local certifiedRMs cache (fallback to prop or call service) ---
  const [certifiedRMsLocal, setCertifiedRMsLocal] = useState<any[]>(
    certifiedRawMaterialCodes || []
  );

  useEffect(() => {
    // If prop provided, prefer it; otherwise try to fetch
    if (certifiedRawMaterialCodes && certifiedRawMaterialCodes.length > 0) {
      setCertifiedRMsLocal(certifiedRawMaterialCodes);
      return;
    }

    const fetchCertified = async () => {
      try {
        // runtime-check + cast to avoid TypeScript error if the function is not present on dtrackItService
        if (
          typeof (dtrackItService as any).getCertifiedRawMatCodes === "function"
        ) {
          const resp: any = await (
            dtrackItService as any
          ).getCertifiedRawMatCodes();
          const data = resp?.data ?? resp ?? [];
          if (Array.isArray(data)) setCertifiedRMsLocal(data);
          else setCertifiedRMsLocal([]);
        } else {
          // service method not available — keep empty fallback
          console.warn(
            "dtrackItService.getCertifiedRawMatCodes not available; falling back to empty certified RM list"
          );
          setCertifiedRMsLocal([]);
        }
      } catch (err) {
        console.error("Failed to fetch certified RM codes:", err);
        setCertifiedRMsLocal([]);
      }
    };
    fetchCertified();
  }, [certifiedRawMaterialCodes]);

  // Helper function to aggregate standards from all matching rows
  const getStandardsForRM = (rmCode: string): string[] => {
    if (!rmCode || !certifiedRMsLocal || certifiedRMsLocal.length === 0) {
      return [];
    }
    // Find ALL rows matching this RM code
    const matched = certifiedRMsLocal.filter(
      (x: any) => (x.material_code || x.materialCode || "").toString().trim() === rmCode.trim()
    );
    if (matched.length === 0) {
      return [];
    }
    // Aggregate all standards from all matching rows
    const standardsSet = new Set<string>();
    matched.forEach((item: any) => {
      const stdRaw = (item.standard || "").trim();
      if (!stdRaw) return;
      // Handle both slash-delimited (RCS/GRS) and comma-delimited formats
      const normalized = stdRaw.replace(/\//g, ",");
      normalized.split(",").forEach((std: string) => {
        const cleaned = std.trim();
        if (cleaned) {
          standardsSet.add(cleaned);
        }
      });
    });
    // Return sorted array
    return Array.from(standardsSet).sort();
  };

  useEffect(() => {
    setEditInitialValues(inventoryData);
    console.log("getUserInfo", getUserInfo);
  }, [inventoryData]);

  const getProductCategory = async () => {
    try {
      const result: any = await dtrackItService.getProductCategory();
      const prdtCategory = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setProductCategory(prdtCategory);
    } catch (err) {
      console.log(`Error in Fetching Organic Farm Standard Code ${err}`);
    }
  };

  const getProductDetails = async () => {
    try {
      const result: any = await dtrackItService.getProductDetailsCode();

      const prdtDetails = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setProductDetails(prdtDetails);
    } catch (err) {
      console.log(`Error in Fetching Organic Farm Standard Code ${err}`);
    }
  };
  const getRawMaterialCodes = async () => {
    try {
      const result: any = await dtrackItService.getRawMaterialCodes();
      const rawMaterials = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setRawMaterialCodes(rawMaterials);
    } catch (err) {
      console.log(`Error in Fetching Organic Farm Standard Code ${err}`);
    }
  };

  useEffect(() => {
    getProductCategory();
    getProductDetails();
    getRawMaterialCodes();
  }, []);

  const updateFilteredRMCodes = (stds: any) => {
    try {
      // Normalize the standards string
      const source = typeof stds === "string" ? stds : "";
      // Accept formats like " (GRS) (RCS)" or just "(GRS/RCS)" or "GRS/RCS"
      // Extract tokens of letters (GRS, RCS, OCS, etc)
      let codes: string[] = [];
      if (source.includes("/")) {
        codes = source
          .split("/")
          .map((s: string) => s.replace(/[()]/g, "").trim());
      } else {
        // find tokens inside parentheses or plain words
        const paren = [...source.matchAll(/\(([^)]+)\)/g)].map((m) => m[1]);
        if (paren.length)
          codes = paren.flatMap((p) =>
            p.split("/").map((s: string) => s.trim())
          );
        else
          codes = source
            .split(",")
            .map((s: string) => s.trim())
            .filter(Boolean);
      }

      // Fallback: if standards are missing/unparseable, show only NA RM codes
      if (!codes.length) {
        console.warn("No standard codes parsed from standards:", stds);

        const naCodes = (certifiedRMsLocal || [])
          .filter((item: any) => (item.standard || "").toUpperCase() === "NA")
          .map((item: any) => item.material_code);

        const rmCodesFiltered = (rawMaterialCodes || []).filter((item: any) =>
          naCodes.includes(item.value)
        );

        setFilteredRMCodes(rmCodesFiltered);
        setFilteredStandards([]); // nothing to preselect
        return;
      }

      // Normal path: filter to the parsed standards (handles "RCS/GRS", "GRS/RCS", etc.)
      setFilteredStandards(
        standardOptions.filter((std) => codes.includes(std.value))
      );

      const approvedSet = new Set(codes);
      const filtered = (certifiedRMsLocal || []).filter((item: any) =>
        (item?.standard || "")
          .split("/")
          .some((part: string) => approvedSet.has(part.trim()))
      );

      const validMaterialCodes = filtered.map(
        (item: any) => item.material_code
      );

      const rmCodesFiltered = (rawMaterialCodes || []).filter((item: any) =>
        validMaterialCodes.includes(item.value)
      );

      setFilteredRMCodes(rmCodesFiltered);
    } catch (error) {
      console.error("Error in updateFilteredRMCodes:", error);

      // Conservative catch fallback: NA codes only
      const naCodes = (certifiedRMsLocal || [])
        .filter((item: any) => item.standard === "NA")
        .map((item: any) => item.material_code);

      const rmCodesFiltered = (rawMaterialCodes || []).filter((item: any) =>
        naCodes.includes(item.value)
      );

      setFilteredRMCodes(rmCodesFiltered);
      setFilteredStandards([]);
    }
  };

  useEffect(() => {
    if (coTEId && !_.isEmpty(rawMaterialCodes)) {
      updateFilteredRMCodes(standards);
    } else {
      setFilteredRMCodes([]);
    }
  }, [
    standards,
    certifiedRawMaterialCodes,
    rawMaterialCodes,
    certifiedRMsLocal,
  ]);
  // Function to reset form state (can be called from parent)
  const resetFormState = () => {
    setIsFormDisabled(false);
    setApiSuccess(false);
    setVolumeAdjustmentError(null);
    // formik.resetForm(); // Reset formik form when modal is closed
  };

  // Expose reset function to parent via ref
  React.useImperativeHandle(formRef, () => ({
    ...formRef.current,
    resetFormState,
  }));

  const handleSubmit = (values: IPostInventory) => {
    setInventoryData(values);
  };

  const negative = ["D"];
  const positive = ["MV", "E", "RMDF", "OF", "GOTS", "F"];
  const neutral = ["O", "PCPD"];
  const formConfig = {
    pageName: "Post-Inventory",
    initialValues: editInitialValues,
    validationSchema: Yup.object().shape({
      volumeAdjustmentReason: Yup.array()
        .of(Yup.string())
        .min(1, "Volume Adjustment Reason is required")
        .required("Volume Adjustment Reason is required"),
      volumeAdjustmentDate: Yup.string().required(
        "Volume Adjustment Date is required"
      ),
      volumeAdjustmentJustification: Yup.string(),
      productCategory: Yup.string().required("Product Category is required"),
      productDetail: Yup.string().required("Product Detail is required"),
      credentialId: Yup.string(),
      // --- NEW: note - certifiedRmCode/standard are not part of this form builder; row-level validation is handled in the table save flow ---
    }),
    sections: [
      {
        title: "",
        fields: [
          {
            label: "Volume Adjustment Reason",
            name: "volumeAdjustmentReason",
            type: "select",
            gridSize: { xs: 12, md: 4 },
            required: true,
            disabled: isFormDisabled,
            props: {
              multiple: true,
              placeholder: "Enter Volume Adjustment Reason",
              variant: "outlined",
              fullWidth: true,
              options: [
                { value: "OB", label: "Opening Balance" },
                { value: "C", label: "Correct" },
                {
                  value: "D",
                  label: "Destroyed, Damaged / Sold as non-certified",
                },
                {
                  value: "PCPD",
                  label: "Product Category/Product Detail update",
                },
                { value: "MV", label: "Missing Volume" },
                { value: "E", label: "Error" },
                {
                  value: "OF",
                  label: "Organic Farm inputs to OCS first processors",
                },
                {
                  value: "GOTS",
                  label:
                    "GOTS certified input previously not uploaded to Trackit",
                },
                {
                  value: "F",
                  label:
                    "Farm production by farm organizations certified to the RAF standards or the RDS",
                },
                { value: "O", label: "Other" },
                { value: "RMDF", label: "Reclaimed Material Declaration Form" },
              ],
              isOptionDisabled: (option: any, value: any) => {
                const sel: string[] = Array.isArray(value) ? value : [];
                const hasOB = sel.includes("OB");
                const hasC = sel.includes("C");

                // OB and C are strict single-selects
                if (hasOB) return option.value !== "OB";
                if (hasC) return option.value !== "C";

                // NEW: if O or PCPD is selected → disable OB and C explicitly
                if (sel.includes("O") || sel.includes("PCPD")) {
                  if (option.value === "OB" || option.value === "C") {
                    return true;
                  }
                }

                // Grouping when neither OB nor C selected
                const base = sel.filter((v) => v !== "O" && v !== "PCPD");
                if (base.length === 0) return false;

                const inNeg = base.some((v) => negative.includes(v));
                const inPos = base.some((v) => positive.includes(v));
                const inNeutral = base.some((v) => neutral.includes(v));

                const allowed = inNeg
                  ? [...negative, ...neutral]
                  : inPos
                  ? [...positive, ...neutral]
                  : inNeutral
                  ? [...negative, ...positive, ...neutral]
                  : [];
                return allowed.length ? !allowed.includes(option.value) : false;
              },
            },
            events: {
              onChange: (updated: any, values: any, setFieldValue: any) => {
                const prev: string[] = values?.volumeAdjustmentReason || [];
                const curr: string[] = Array.isArray(updated) ? updated : [];

                if (curr.length === 0) {
                  setFieldValue("volumeAdjustmentJustification", "");
                  setFieldValue("credentialId", "");
                  return;
                }

                const added = curr.find((v) => !prev.includes(v));

                // If OB or C selected → force single selection and clear dependents
                if (curr.includes("OB") || added === "OB") {
                  setFieldValue("volumeAdjustmentReason", ["OB"]);
                  setFieldValue("volumeAdjustmentJustification", "");
                  setFieldValue("credentialId", "");
                  return;
                }
                if (curr.includes("C") || added === "C") {
                  setFieldValue("volumeAdjustmentReason", ["C"]);
                  setFieldValue("volumeAdjustmentJustification", "");
                  setFieldValue("credentialId", "");
                  return;
                }

                // Remove OB/C if present with other choices
                let next = curr.filter((v) => v !== "OB" && v !== "C");

                // Determine active group
                const base = next.filter((v) => v !== "O" && v !== "PCPD");
                let group: "negative" | "positive" | null = null;
                if (added && added !== "O" && added !== "PCPD") {
                  group = negative.includes(added)
                    ? "negative"
                    : positive.includes(added)
                    ? "positive"
                    : null;
                } else if (base.length) {
                  const first = base[0];
                  group = negative.includes(first)
                    ? "negative"
                    : positive.includes(first)
                    ? "positive"
                    : null;
                }

                if (group) {
                  const allowed =
                    group === "negative"
                      ? [...negative, ...neutral]
                      : [...positive, ...neutral];
                  const filtered = next.filter((v) => allowed.includes(v));
                  if (filtered.length !== next.length) {
                    setFieldValue("volumeAdjustmentReason", filtered);
                    next = filtered;
                  }
                }

                // Clear dependents if triggers not present
                if (!next.includes("O") && !next.includes("PCPD"))
                  setFieldValue("volumeAdjustmentJustification", "");
                const hasRmdfGots = next.some(
                  (v) => v === "RMDF" || v === "GOTS"
                );
                if (!hasRmdfGots) setFieldValue("credentialId", "");
              },
            },
          },
          {
            label: "Volume Adjustment Date",
            name: "volumeAdjustmentDate",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: isFormDisabled,
            props: {
              placeholder: "Select Volume Adjustment Date",
            },
          },
          {
            label: "Volume Adjustment Justification",
            name: "volumeAdjustmentJustification",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (values: any) => {
              const selectedReasons = values?.volumeAdjustmentReason || [];
              return (
                selectedReasons.includes("O") ||
                selectedReasons.includes("PCPD")
              );
            },
            visible: (values: any) => {
              const selectedReasons = values?.volumeAdjustmentReason || [];
              return (
                selectedReasons.includes("O") ||
                selectedReasons.includes("PCPD")
              );
            },
            disabled: isFormDisabled,
            props: {
              placeholder: "Enter Volume Adjustment Justification",
            },
            events: {
              onChange: (value: any, values: any, setFieldValue: any) => {
                // Clear volumeAdjustmentJustification value when "O" is deselected
                const selectedReasons = values?.volumeAdjustmentReason || [];
                if (
                  !selectedReasons.includes("O") &&
                  !selectedReasons.includes("PCPD")
                ) {
                  setFieldValue("volumeAdjustmentJustification", "");
                }
              },
            },
          },
          {
            label: "Product Category",
            name: "productCategory",
            type: "select",
            gridSize: { xs: 12, md: 4 },
            required: true,
            disabled: isFormDisabled,
            props: {
              placeholder: "Select Product Category",
              fullWidth: true,
              options: productCategory, // API-based options
            },
          },
          {
            label: "Product Detail",
            name: "productDetail",
            type: "select",
            gridSize: { xs: 12, md: 4 },
            required: true,
            disabled: isFormDisabled,
            props: {
              placeholder: "Select Product Detail",
              fullWidth: true,
              options: productDetails, // API-based options
            },
          },
          {
            label: "Credential ID / Number (for RMDF & GOTS)",
            name: "credentialId",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: (values: any) => {
              const selectedReasons = values?.volumeAdjustmentReason || [];
              const rmdfGotsReasons = ["RMDF", "GOTS"];
              return selectedReasons.some((reason: string) =>
                rmdfGotsReasons.includes(reason)
              );
            },
            visible: (values: any) => {
              const selectedReasons = values?.volumeAdjustmentReason || [];
              const rmdfGotsReasons = ["RMDF", "GOTS"];
              return selectedReasons.some((reason: string) =>
                rmdfGotsReasons.includes(reason)
              );
            },
            disabled: isFormDisabled,
            props: {
              placeholder: "Enter Credential ID / Number",
              variant: "outlined",
              fullWidth: true,
            },
            events: {
              onChange: (value: any, values: any, setFieldValue: any) => {
                // Clear credentialId value when RMDF/GOTS are deselected
                const selectedReasons = values?.volumeAdjustmentReason || [];
                const rmdfGotsReasons = ["RMDF", "GOTS"];
                const hasRmdfGots = selectedReasons.some((reason: string) =>
                  rmdfGotsReasons.includes(reason)
                );

                if (!hasRmdfGots) {
                  setFieldValue("credentialId", "");
                }
              },
            },
          },
        ],
      },
    ],
    handleSubmit: handleSubmit,
  };

  const standardOptions = [
    { label: "Organic Content Standard (OCS)", value: "OCS" },
    { label: "Responsible Down Standard (RDS)", value: "RDS" },
    { label: "Responsible Alpaca Standard (RAS)", value: "RAS" },
    { label: "Global Recycled Standard (GRS)", value: "GRS" },
    { label: "Responsible Wool Standard (RWS)", value: "RWS" },
    { label: "Content Claim Standard (CCS)", value: "CCS" },
    { label: "Recycled Claim Standard (RCS)", value: "RCS" },
    { label: "Responsible Mohair Standard (RMS)", value: "RMS" },
  ];

  const initialColumns = (rmCodes: any[] = []): EditableColumnDef<any>[] => [
    {
      accessorKey: "rawMaterialCode",
      header: "Raw Material Code",
      Header: (
        <span>
          {" "}
          <i style={{ color: "red" }}>* </i>Raw Material Code
        </span>
      ),
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomSelect
          name="rawMaterialCode"
          value={value}
          placeholder="Enter value"
          options={rmCodes} //{rawMaterialCodes}
          enableSearch={true}
          onChange={(_e: any) => {
            const newRm = _e.target.value;
            onChange(newRm);

            // Use helper function to get ALL standards for this RM
            const standardsList = getStandardsForRM(newRm);

            if (standardsList.length === 1) {
              // Single standard -> auto-fill and disable
              setData((prev) => {
                const next = [...prev];
                const idx =
                  typeof rowIndex === "number" ? rowIndex : next.length - 1;
                if (idx >= 0) {
                  next[idx] = {
                    ...next[idx],
                    standard: standardsList[0],
                    standardDisabled: true
                  };
                }
                return next;
              });
              formik.setFieldValue("standard", standardsList[0]);
            } else if (standardsList.length > 1) {
              // Multiple standards -> enable dropdown with filtered options
              setData((prev) => {
                const next = [...prev];
                const idx =
                  typeof rowIndex === "number" ? rowIndex : next.length - 1;
                if (idx >= 0) {
                  next[idx] = {
                    ...next[idx],
                    standard: "",
                    standardDisabled: false
                  };
                }
                return next;
              });
              formik.setFieldValue("standard", "");

              // Show only the standards available for this RM
              const availableStandards = standardOptions.filter((std) =>
                standardsList.includes(std.value)
              );
              setFilteredStandards(availableStandards);
            } else {
              // No standards found
              setData((prev) => {
                const next = [...prev];
                const idx = typeof rowIndex === "number" ? rowIndex : next.length - 1;
                if (idx >= 0) {
                  next[idx] = {
                    ...next[idx],
                    standard: "",
                    standardDisabled: false
                  };
                }
                return next;
              });
              formik.setFieldValue("standard", "");
              setFilteredStandards([]);
            }
          }}
        />
      ),
    },
    {
      accessorKey: "standard",
      header: "Standard",
      renderEditCell: ({ value, onChange, rowIndex }) => {
        const locked = !!data?.[rowIndex]?.standardDisabled;

        if (locked) {
          return (
            <CustomTextField
              name="standard"
              value={value}
              placeholder="Standard"
              readOnly
              disabled
            />
          );
        }

        // Get standards for current RM and filter options
        const currentRm = data?.[rowIndex]?.rawMaterialCode;
        let optionsToShow = standardOptions;

        if (currentRm) {
          const standardsForCurrentRm = getStandardsForRM(currentRm);
          if (standardsForCurrentRm.length > 0) {
            // Show only the standards available for this RM
            optionsToShow = standardOptions.filter((std) =>
              standardsForCurrentRm.includes(std.value)
            );
          }
        }

        return (
          <CustomSelect
            name="standard"
            value={value}
            options={optionsToShow}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Select Standard"
          />
        );
      },
    },
    {
      accessorKey: "volumeAdjustment",
      header: "Volume Adjustment",
      renderEditCell: ({ value, onChange }) => (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            display: "flex",
            alignItems: "center",
            minHeight: "40px", // Match the height of other fields
          }}
        >
          <Tooltip
            title={volumeAdjustmentError || ""}
            open={!!volumeAdjustmentError}
            placement="top"
            arrow
            PopperProps={{
              sx: {
                "& .MuiTooltip-tooltip": {
                  backgroundColor: "#d32f2f",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 500,
                  padding: "8px 12px",
                  borderRadius: "4px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  maxWidth: "300px",
                },
                "& .MuiTooltip-arrow": {
                  color: "#d32f2f",
                },
              },
            }}
            sx={{ width: "100%" }}
          >
            <CustomTextField
              name="volumeAdjustment"
              value={value}
              type="text"
              onChange={(e) => {
                const value = e.target.value;
                if (/^-?\d*\.?\d{0,2}$/.test(value)) {
                  onChange(e.target.value);
                }
              }}
              placeholder="Enter value (Format 67.35)"
              sx={{
                width: "100%",
                marginTop: (theme) => theme.spacing(-1.875), // MUI spacing system for responsive design
                "& .MuiOutlinedInput-root": {
                  borderColor: volumeAdjustmentError ? "#d32f2f" : undefined,
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: volumeAdjustmentError ? "#d32f2f" : undefined,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: volumeAdjustmentError ? "#d32f2f" : undefined,
                  },
                },
              }}
            />
          </Tooltip>
          {volumeAdjustmentError && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                right: "8px",
                transform: "translateY(-50%)",
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                backgroundColor: "#d32f2f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "12px",
                fontWeight: "bold",
                zIndex: 10,
              }}
            >
              !
            </Box>
          )}
        </Box>
      ),
    },
  ];

  const [columns, setColumns] =
    useState<EditableColumnDef<any>[]>(initialColumns);

  const initialData: IInventoryUpdateRawMaterialData[] = [
    {
      rawMaterialCode: "",
      standard: "",
      volumeAdjustment: "",
      standardDisabled: false,
    },
  ];
  const [data, setData] = useState<IInventoryUpdateRawMaterialData[]>([]);
  const [newRowIndices, setNewRowIndices] = useState<number[]>([]);

  useEffect(() => {
    setColumns(initialColumns(filteredRMCodes));
  }, [filteredRMCodes, volumeAdjustmentError]);

  const mapFormDataToPayload = (
    formRef: any,
    tableData: IInventoryUpdateRawMaterialData[],
    countryStateData: any
  ) => {
    // Get only the newly added row from tableData
    const newRow = tableData[tableData.length - 1];

    return {
      inventoryType: formRef?.current?.values?.volumeAdjustmentReason,
      productCategory: formRef?.current?.values?.productCategory,
      productDetail: formRef?.current?.values?.productDetail,
      certifiedRmCode: newRow?.rawMaterialCode,
      standard: newRow?.standard,
      balance: parseFloat(newRow?.volumeAdjustment),
      country: countryStateData?.country,
      state: countryStateData?.state,
      justificationReason:
        formRef?.current?.values?.volumeAdjustmentJustification,
      credentialIdNo: formRef?.current?.values?.credentialId,
      transactionDate: formRef?.current?.values?.volumeAdjustmentDate,
    };
  };

  // const handleSave = async () => {
  //   const payload = mapFormDataToPayload(formRef, data, editInitialValues);
  //   const queryParams = new URLSearchParams({
  //     teId: "TE-00061237",
  //     tcCbLicensingCode: "CB-ITS",
  //   }).toString();
  //   const finalUrl = `${}?${queryParams}`;
  //   const result: any = await dtrackItService.postInventoryUpdateRawMaterial(payload);
  //   if(result.status === 200) {
  //     notify("Inventory Update Raw Material Saved Successfully", "success");
  //   } else {
  //     notify("Unable to Save", "error");
  //   }
  //   console.log("This is the payload", payload);
  // }

  // --- CHANGED: stronger validation & RM/standard checks here ---
  const handleSaveRow = async (rowData?: any) => {
    if (!rowData && data.length === 0) {
      notify("No row to save. Please add a raw material row first.", "error");
      return;
    }

    const currentRow = rowData || data[data.length - 1] || {};
    // Require rawMaterialCode (RM) explicitly
    if (
      !currentRow.rawMaterialCode ||
      currentRow.rawMaterialCode.trim() === ""
    ) {
      notify("Raw Material Code is required.", "error");
      setVolumeAdjustmentError("Raw Material Code is required.");
      return;
    }

    // Require volumeAdjustment value present (except for some cases handled below)
    if (
      currentRow.volumeAdjustment === undefined ||
      currentRow.volumeAdjustment === null ||
      (currentRow.volumeAdjustment.toString &&
        currentRow.volumeAdjustment.toString().trim() === "")
    ) {
      notify("Volume adjustment value is required.", "error");
      setVolumeAdjustmentError("Volume adjustment value is required.");
      return;
    }

    // First, submit the form to save form data
    if (formRef.current) {
      formRef.current.submitForm();
    }

    // Clear previous validation errors
    setVolumeAdjustmentError(null);

    // Figure out standards for this RM
    const rm = currentRow.rawMaterialCode;
    const standardsList = getStandardsForRM(rm);

    // If multiple standards exist, user must explicitly choose one
    const needsExplicitChoice = standardsList.length > 1;
    const isMissingStandard = !currentRow.standard || currentRow.standard.trim() === "";

    if (needsExplicitChoice && isMissingStandard) {
      setVolumeAdjustmentError("Please select a Standard for the selected RM.");
      notify("Please select a Standard for the selected RM.", "error");
      return;
    }

    // General guard: standard must be present for any RM
    if (!currentRow.standard || currentRow.standard.trim() === "") {
      setVolumeAdjustmentError("Standard is required.");
      notify("Standard is required.", "error");
      return;
    }

    // Get the current row data (either from parameter or from editing row)
    const currentRowFinal =
      rowData || (editingRowIndex !== null ? data[editingRowIndex] : null);

    // If no valid row data, return early
    if (!currentRowFinal) {
      notify("No valid row data found", "error");
      return;
    }

    const volumeAdjustmentValue = parseFloat(
      currentRowFinal?.volumeAdjustment || 0
    );

    // Get the selected volume adjustment reasons from the form
    const selectedReasons =
      formRef?.current?.values?.volumeAdjustmentReason || [];

    // Define negative and positive reason groups
    const negativeReasons = ["D"];
    const positiveReasons = ["OB", "C", "MV", "E", "OF", "GOTS", "F", "RMDF"];

    // Check if any negative reasons are selected
    const hasNegativeReason = selectedReasons.some((reason: string) =>
      negativeReasons.includes(reason)
    );
    const hasPositiveReason = selectedReasons.some((reason: string) =>
      positiveReasons.includes(reason)
    );

    // Validation logic
    let validationError = null;

    // Special validation for "C" - must be exactly 0
    if (selectedReasons.includes("C")) {
      // Check for empty/null values first
      if (
        !currentRowFinal.volumeAdjustment ||
        currentRowFinal.volumeAdjustment.toString().trim() === ""
      ) {
        validationError =
          "Volume adjustment value is required and must be exactly 0 when 'Correct' is selected";
      } else if (volumeAdjustmentValue !== 0) {
        validationError =
          "Volume adjustment value must be exactly 0 when 'Correct' is selected";
      }
    } else if (hasNegativeReason && volumeAdjustmentValue >= 0) {
      validationError =
        "Volume adjustment value must be negative for selected reasons";
    } else if (
      hasPositiveReason &&
      !hasNegativeReason &&
      volumeAdjustmentValue < 0
    ) {
      validationError =
        "Volume adjustment value must be positive for selected reasons";
    }

    // If validation fails, show error and prevent save
    if (validationError) {
      setVolumeAdjustmentError(validationError);
      // notify(validationError, "error");
      return; // Prevent save action
    }

    // If validation passes, proceed with save
    setPopUpBtnName("Confirm");
    setPopUpTitle("Confirm Inventory Update ?");
    setPopUpMessage(
      `Please confirm that you would like to proceed with creating this ledger entry. This action cannot be undone.`
    );
    setShowConfirmButton(true);
    setDialogOpen(true);
  };

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    // If standard is locked for this row, ignore any attempts to change it
    if (columnId === "standard" && data?.[rowIndex]?.standardDisabled) {
      return;
    }

    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );

    // Clear volume adjustment validation error when user changes the value
    if (columnId === "volumeAdjustment") {
      setVolumeAdjustmentError(null);
    }

    // If user changed rawMaterialCode inline, we may want to auto-fill standard client-side:
    if (columnId === "rawMaterialCode") {
      const standardsList = getStandardsForRM(value);
      // If single standard, auto-fill
      if (standardsList.length === 1) {
        setData((prev) =>
          prev.map((row, idx) =>
            idx === rowIndex
              ? { ...row, standard: standardsList[0], standardDisabled: true }
              : row
          )
        );
        formik.setFieldValue("standard", standardsList[0]);
      } else {
        // clear selected standard - user must choose from multiple options
        setData((prev) =>
          prev.map((row, idx) =>
            idx === rowIndex
              ? { ...row, standard: "", standardDisabled: false }
              : row
          )
        );
        formik.setFieldValue("standard", "");
      }
    }
  };

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setVolumeAdjustmentError(null); // Clear validation errors when starting to edit
  };

  const handleCancelEdit = () => {
    if (editingRowIndex !== null) {
      handleDeleteRow(editingRowIndex);
    }
    setEditingRowIndex(-1);
    formik.resetForm();
    setIsRestrictPopUpSave(false);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = [...data];
    updatedData.splice(rowIndex, 1);
    setData(updatedData);
  };

  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);

  const validateRequiredFields = async () => {
    // Get current values from the FormBuilder form
    const currentValues = formRef.current?.values || {};

    try {
      // Use the existing Yup validation schema from formConfig
      await formConfig.validationSchema.validate(currentValues, {
        abortEarly: false,
      });
      return true;
    } catch (validationError: any) {
      // Extract the first validation error message
      const firstError =
        validationError.errors?.[0] || "Please fill in all required fields";
      notify(firstError, "error");
      return false;
    }
  };

  const handleAddNewRow = async () => {
    // Validate required fields before allowing row creation
    const isValid = await validateRequiredFields();
    if (!isValid) {
      return; // Don't create row if validation fails
    }

    const newRow: any = {
      rawMaterialCode: "",
      standard: "",
      volumeAdjustment: "",
    };
    setData((prevData: any) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setVolumeAdjustmentError(null); // Clear validation errors when adding new row
    setIsRestrictPopUpSave(true);
  };

  const formik = useFormik<any>({
    initialValues: {
      rawMaterialCode: "",
      standard: "",
      volumeAdjustment: "",
    },
    validationSchema: Yup.object().shape({}),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCancelSave = async () => {
    setDialogOpen(false);
    setIsRestrictPopUpSave(false);
  };

  const handleConfirmSave = async () => {
    // Disable form fields immediately when confirm is clicked
    setIsFormDisabled(true);

    if (editingRowIndex == null || editingRowIndex === -1 || !formik.isValid)
      return;
    setDialogOpen(false);
    // Merge: preserve existing row values, overlay form edits
    const idx = editingRowIndex as number;
    const baseRow = data[idx];
    let updatedRow = { ...baseRow, ...formik.values };
    // If standard is locked, enforce preservation
    if (baseRow?.standardDisabled) {
      updatedRow.standard = baseRow.standard;
    }
    const newData = data.map((row, index) =>
      index === idx ? updatedRow : row
    );
    setData(newData);
    const newIndex = data.length;
    // setEditingRowIndex(newIndex);
    setNewRowIndices((prev) => [...prev, newIndex]);
    const payload = mapFormDataToPayload(formRef, newData, countryStateData);
    const queryParams = new URLSearchParams({
      teId: coTEId,
      tcCbLicensingCode: cbLicensingCode,
    }).toString();
    const finalUrl = `${MLAppConfig.CREATE_INVENTORY_DASHBOARD}?${queryParams}`;
    const result: any = await inventoryupdatepost(finalUrl, payload);

    if (result.status === 200) {
      setApiSuccess(true);
      setEditingRowIndex(newIndex);
      setIsRestrictPopUpSave(false);
      // refreshInventory();
    } else if (result.status === 400) {
      setPopUpBtnName("Cancel");
      setPopUpTitle("Inventory Update Failed");
      setPopUpMessage(result.data);
      setShowConfirmButton(false);
      setDialogOpen(true);
      setIsRestrictPopUpSave(true);
      // notify(result.data.message, "error");
    }
    setIsFormDisabled(false);
    // if status is 400 then show error message in the modalpopup

    // console.log("This is the country state data", countryStateData);
    // console.log("This is the payload", payload);

    // formik.resetForm(); // Removed this line to prevent form reset
  };

  return (
    <>
      {inventoryLoading ? (
        <LoaderComponent />
      ) : (
        <>
          {/* Organization Information Display */}
          <Box
            sx={{ mb: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
          >
            <Typography variant="body1" color="textSecondary">
              Organization: {organizationName} (TE ID: {coTEId})
            </Typography>
          </Box>

          <FormBuilder formConfig={formConfig} formRef={formRef} />
          <Box
            display="flex"
            mb={3}
            justifyContent="space-between"
            width="100%"
          >
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ mb: 1 }}
            ></Typography>
            <StyledButton
              disabled={!formik.isValid || isRestrictPopUpSave}
              onClick={handleAddNewRow}
              sx={{ mb: 2 }}
            >
              Add Raw Material
            </StyledButton>
          </Box>

          <EditableTable
            data={data}
            columns={columns}
            onSaveRow={handleSaveRow}
            onCellChange={handleCellChange}
            editingRowIndex={editingRowIndex}
            onEditRow={handleEditRow}
            onCancelEdit={handleCancelEdit}
            onDeleteRow={handleDeleteRow}
            showSuccessIcon={apiSuccess}
          />
          <AlertPopup
            open={dialogOpen}
            title={popUpTitle}
            message={popUpMessage}
            onClose={handleCancelSave}
            onConfirm={handleConfirmSave}
            confirmText={popUpBtnName}
            cancelText="Cancel"
            showConfirmButton={showConfirmButton}
          />
        </>
      )}
    </>
  );
};

export default UpdateInventoryDetails;
